import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { jobAbuseAlertsAPI } from '../services/api';
import { fmtDateTime, fmtTime } from '../utils/time';
import {
    FiAlertTriangle, FiRefreshCw, FiUser, FiPackage,
    FiDollarSign, FiChevronDown, FiChevronUp, FiPlayCircle, FiZap, FiCopy
} from 'react-icons/fi';
import '../styles/JobAbuseAlerts.css';

const SEV = {
    high:   { label: 'NGUY HIỂM CAO',        cls: 'sev-high',   icon: '🔴' },
    medium: { label: 'NGHI VẤN TRUNG BÌNH',  cls: 'sev-medium', icon: '🟠' },
    low:    { label: 'CẢNH BÁO NHẸ',         cls: 'sev-low',    icon: '🟡' },
};

const RULE = {
    compound:     { label: 'COMPOUND (Item + Reward)',  icon: '🎁', cls: 'rule-compound' },
    rapid_reward: { label: 'RAPID REWARD (cùng nghề)', icon: '⚡', cls: 'rule-rapid'    },
    dup_reward:   { label: 'DUP EXPLOIT (trùng tiền)', icon: '🪞', cls: 'rule-dup'      },
};

const JOB_LABEL = {
    pizza:'🍕 Pizza', truck:'🚚 Trucker', mining:'⛏️ Mining', farm:'🌾 Farm',
    fish:'🎣 Fishing', hunt:'🦌 Hunting', taxi:'🚖 Taxi', garbage:'🗑️ Garbage',
    lumber:'🌲 Lumber', mechanic:'🔧 Mechanic', oil:'⛽ Oil',
    weed:'🌿 Weed', meth:'🧪 Meth', other:'❓ Khác',
};

const $ = (n) => n == null ? '—' : '$' + Math.round(n).toLocaleString('vi-VN');

const JobAbuseCard = ({ a }) => {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const sev = SEV[a.severity] || SEV.low;
    const rule = RULE[a.ruleType] || RULE.compound;
    const jobLabel = JOB_LABEL[a.dominantJobTag] || `❓ ${a.dominantJobTag || 'unknown'}`;

    const isDup    = a.ruleType === 'dup_reward';
    const isRapid  = a.ruleType === 'rapid_reward';

    const summary = isDup
        ? <>nhận <strong>{a.rewardCount} lần × {$(a.dupAmount)}</strong> CÙNG reason <code>{a.primaryReason}</code> trong {a.dupGapSec}s — <strong className="jaa-warn-text">khả năng cao là DUP EXPLOIT</strong></>
        : isRapid
            ? <>nhận <strong>{a.rewardCount} lần thưởng</strong> reason <code>{a.primaryReason}</code> trong {a.timeWindowSec}s ({$(a.totalAmount)})</>
            : <>nhận <span className="jaa-stat-pill jaa-pill-item"><FiPackage /> {a.itemCount} item</span> & <span className="jaa-stat-pill jaa-pill-money"><FiDollarSign /> {a.rewardCount} lần thưởng</span> trong {Math.round(a.timeWindowSec / 60)} phút</>;

    return (
        <div className={`jaa-card ${sev.cls} ${rule.cls}`}>
            <div className="jaa-head">
                <div className="jaa-head-left">
                    <span className="jaa-sev-badge">{sev.icon} {sev.label}</span>
                    <span className="jaa-rule-tag">{rule.icon} {rule.label}</span>
                    <span className="jaa-job-tag">{jobLabel}</span>
                    {a.totalAmount > 0 && <span className="jaa-amount">{$(a.totalAmount)}</span>}
                </div>
                <div className="jaa-head-right">
                    <strong>{fmtDateTime(a.windowStart)}</strong>
                    <span className="jaa-span">window {a.timeWindowSec}s</span>
                </div>
            </div>

            <div className="jaa-summary">
                <button
                    className="jaa-player-link"
                    onClick={() => a.citizenId && navigate(`/fivem-player/${a.citizenId}`)}
                    disabled={!a.citizenId}
                >
                    <FiUser /> <strong>{a.playerName || '?'}</strong>
                </button>
                <span> {summary}</span>
            </div>

            <div className="jaa-ids">
                <code>CID: {a.citizenId || '—'}</code>
                {a.serverId && <code>ID: {a.serverId}</code>}
                {a.distinctReasons > 1 && (
                    <code className="jaa-info-pill">{a.distinctReasons} reason khác nhau</code>
                )}
            </div>

            <button className="jaa-expand-btn" onClick={() => setExpanded(v => !v)}>
                {expanded ? <FiChevronUp /> : <FiChevronDown />}
                {expanded ? 'Thu gọn' : `Xem chi tiết ${(a.itemEvents?.length || 0) + (a.rewardEvents?.length || 0)} event`}
            </button>

            {expanded && (
                <div className="jaa-detail">
                    <div className="jaa-detail-cols">
                        {a.itemCount > 0 && (
                            <div className="jaa-col">
                                <h4><FiPackage /> Item vào kho ({a.itemCount})</h4>
                                <table className="jaa-table">
                                    <thead><tr><th>Giờ</th><th>Item</th><th>Nguồn</th></tr></thead>
                                    <tbody>
                                        {(a.itemEvents || []).map((e, i) => (
                                            <tr key={i}>
                                                <td className="ts-cell">{fmtTime(e.time)}</td>
                                                <td className="item-cell">{e.item || '—'}</td>
                                                <td className="src-cell">{e.source || e.reason || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="jaa-col">
                            <h4><FiDollarSign /> Chi tiết {a.rewardCount} lần +tiền trong window</h4>
                            <table className="jaa-table">
                                <thead>
                                    <tr><th>#</th><th>Thời gian</th><th>Số tiền</th><th>Loại tiền</th><th>Lý do</th><th>Cách event trước</th></tr>
                                </thead>
                                <tbody>
                                    {(a.rewardEvents || []).map((e, i, arr) => {
                                        const prev = i > 0 ? arr[i - 1] : null;
                                        const gap = prev ? Math.round((new Date(e.time) - new Date(prev.time)) / 1000) : null;
                                        const gapCls = gap == null ? '' : gap <= 5 ? 'gap-hot' : gap <= 30 ? 'gap-warm' : '';
                                        return (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td className="ts-cell">{fmtTime(e.time)}</td>
                                                <td className="amt-cell">+{$(e.amount)}</td>
                                                <td><span className="cur-pill">{e.currency || 'cash'}</span></td>
                                                <td className="reason-cell">{e.reason}</td>
                                                <td className={`gap-cell ${gapCls}`}>{gap == null ? '—' : `+${gap}s`}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const JobAbuseAlerts = () => {
    const [alerts, setAlerts]   = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [filter, setFilter]     = useState('');
    const [jobFilter, setJobFilter] = useState('');
    const [ruleFilter, setRuleFilter] = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [a, s] = await Promise.all([
                jobAbuseAlertsAPI.list({
                    severity: filter || undefined,
                    ruleType: ruleFilter || undefined,
                    jobTag:   jobFilter || undefined,
                    limit:    500,
                }),
                jobAbuseAlertsAPI.summary(),
            ]);
            setAlerts(a.data.alerts || []);
            setSummary(s.data);
        } catch (e) {
            console.error(e);
            toast.error('Không tải được job-abuse alerts');
        } finally { setLoading(false); }
    }, [filter, jobFilter, ruleFilter]);

    const triggerScan = useCallback(async (full) => {
        setScanning(true);
        try {
            if (full) {
                await jobAbuseAlertsAPI.scanAll();
                toast.info('⚙️ Full-history scan triggered — chạy nền, vài phút sau refresh');
                setTimeout(fetchAll, 30_000);
            } else {
                await jobAbuseAlertsAPI.scan();
                toast.info('Incremental scan triggered');
                setTimeout(fetchAll, 10_000);
            }
        } catch (e) {
            toast.error('Scan thất bại');
        } finally { setScanning(false); }
    }, [fetchAll]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Auto-refresh mỗi 30s
    useEffect(() => {
        const t = setInterval(fetchAll, 30_000);
        return () => clearInterval(t);
    }, [fetchAll]);

    return (
        <div className="jaa-page">
            <div className="jaa-header">
                <div>
                    <h1 className="jaa-title">
                        <FiAlertTriangle className="jaa-icon" />
                        Anti-Hack Job — 3 lớp phát hiện exploit
                    </h1>
                    <p className="jaa-sub">
                        <strong>Compound:</strong> ≥2 item + ≥3 thưởng / 5ph &nbsp;·&nbsp;
                        <strong>Rapid:</strong> ≥3 lần thưởng cùng nghề / 60s &nbsp;·&nbsp;
                        <strong>Dup:</strong> ≥2 thưởng trùng số tiền + cùng nghề / 10s (exploit duplicate)
                    </p>
                </div>
                <div className="jaa-actions">
                    <button className="jaa-btn jaa-btn-scan-all" onClick={() => triggerScan(true)} disabled={scanning}>
                        <FiZap /> Quét TOÀN BỘ history
                    </button>
                    <button className="jaa-btn jaa-btn-scan" onClick={() => triggerScan(false)} disabled={scanning}>
                        <FiPlayCircle /> Scan ngay
                    </button>
                    <button className="jaa-btn" onClick={fetchAll} disabled={loading}>
                        <FiRefreshCw className={loading ? 'spinning' : ''} /> Refresh
                    </button>
                </div>
            </div>

            {summary && (
                <>
                    <div className="jaa-summary-row">
                        <button className={`jaa-stat ${filter==='' ? 'active' : ''}`} onClick={() => setFilter('')}>
                            <span className="stat-num">{summary.total}</span>
                            <span className="stat-label">Tổng nghi vấn</span>
                        </button>
                        <button className={`jaa-stat sev-high ${filter==='high' ? 'active' : ''}`} onClick={() => setFilter('high')}>
                            <span className="stat-num">{summary.severity.high}</span>
                            <span className="stat-label">🔴 Nguy hiểm cao</span>
                            <span className="stat-sub">{$(summary.totalAmountBySeverity.high)}</span>
                        </button>
                        <button className={`jaa-stat sev-medium ${filter==='medium' ? 'active' : ''}`} onClick={() => setFilter('medium')}>
                            <span className="stat-num">{summary.severity.medium}</span>
                            <span className="stat-label">🟠 Trung bình</span>
                            <span className="stat-sub">{$(summary.totalAmountBySeverity.medium)}</span>
                        </button>
                        <button className={`jaa-stat sev-low ${filter==='low' ? 'active' : ''}`} onClick={() => setFilter('low')}>
                            <span className="stat-num">{summary.severity.low}</span>
                            <span className="stat-label">🟡 Nhẹ</span>
                            <span className="stat-sub">{$(summary.totalAmountBySeverity.low)}</span>
                        </button>
                        <div className="jaa-stat jaa-stat-meta">
                            <span className="stat-label">Lần scan cuối</span>
                            <span className="stat-sub">{summary.lastScanAt ? fmtTime(summary.lastScanAt) : '—'}</span>
                        </div>
                    </div>

                    {/* Rule type filter row */}
                    <div className="jaa-job-row">
                        <button
                            className={`jaa-job-chip ${ruleFilter==='' ? 'active' : ''}`}
                            onClick={() => setRuleFilter('')}
                        >
                            Tất cả rule
                        </button>
                        {summary.byRule && Object.entries(summary.byRule).map(([rt, count]) => (
                            <button
                                key={rt}
                                className={`jaa-job-chip ${ruleFilter===rt ? 'active' : ''}`}
                                onClick={() => setRuleFilter(rt)}
                            >
                                {RULE[rt]?.icon} {RULE[rt]?.label || rt}
                                <span className="chip-count">{count}</span>
                            </button>
                        ))}
                    </div>

                    {summary.byJob && summary.byJob.length > 0 && (
                        <div className="jaa-job-row">
                            <button
                                className={`jaa-job-chip ${jobFilter==='' ? 'active' : ''}`}
                                onClick={() => setJobFilter('')}
                            >
                                Tất cả job
                            </button>
                            {summary.byJob.map(j => (
                                <button
                                    key={j.jobTag}
                                    className={`jaa-job-chip ${jobFilter===j.jobTag ? 'active' : ''}`}
                                    onClick={() => setJobFilter(j.jobTag)}
                                >
                                    {JOB_LABEL[j.jobTag] || j.jobTag}
                                    <span className="chip-count">{j.count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}

            {loading && alerts.length === 0 ? (
                <div className="jaa-loading"><div className="loading-spinner" /></div>
            ) : alerts.length === 0 ? (
                <div className="jaa-empty">✅ Không có alert nào ở filter này</div>
            ) : (
                <div className="jaa-list">
                    {alerts.map(a => <JobAbuseCard key={a._id} a={a} />)}
                </div>
            )}
        </div>
    );
};

export default JobAbuseAlerts;
